/**
 * Error message translator utility
 * Maps backend error codes and Chinese error messages to i18n keys for proper localization
 */

// Map of error codes to i18n keys
const ERROR_CODE_MAP = {
  'UNAUTHORIZED': 'errors.unauthorized',
  'TOKEN_EXPIRED': 'errors.tokenExpired',
  'INVALID_TOKEN': 'errors.invalidToken',
  'INVALID_TOKEN_TYPE': 'errors.invalidTokenType',
  'INSUFFICIENT_BALANCE': 'errors.insufficientBalance',
  'OPERATION_FAILED': 'errors.operationFailed',
  'INVALID_PARAMS': 'errors.invalidParams',
  'SERVICE_UNAVAILABLE': 'errors.serviceUnavailable',
};

// Map of Chinese error messages to i18n keys
const ERROR_MESSAGE_MAP = {
  // Swap/Exchange errors
  'USDC.e 余额不足，无法完成兑换': 'errors.usdceInsufficientBalance',
  'Privy 钱包缺少 wallet_id，无法完成兑换': 'errors.privyWalletMissingId',
  '缺少私钥，无法执行兑换': 'errors.missingPrivateKey',
  '缺少私钥，无法执行 Safe 兑换': 'errors.missingPrivateKeySafe',
  '未配置 Builder 签名，无法通过 Relayer 执行 Safe 兑换': 'errors.builderConfigMissing',
  'Safe 兑换交易未在规定时间内确认': 'errors.safeSwapTimeout',
  'approve 失败': 'errors.approveFailed',
  'swap 交易失败': 'errors.swapFailed',
  'Privy 交易失败': 'errors.privyTransactionFailed',
  
  // Wallet errors
  '钱包地址与私钥不匹配': 'errors.walletAddressMismatch',
  '缺少私钥，无法保存非 Privy 钱包': 'errors.missingPrivateKeyForNonPrivy',
  
  // Transfer errors
  '余额不足': 'errors.insufficientBalance',
  '转账失败': 'errors.transferFailed',
  '交易超时': 'errors.transactionTimeout',
  '余额不足，原生 USDC 和 USDC.e 余额均为 0': 'errors.bothBalancesZero',
  '实际转账金额过小，无法执行': 'errors.transferAmountTooSmall',
  '转账金额必须大于 0': 'errors.transferAmountMustBePositive',
  
  // General errors
  '请求失败': 'errors.requestFailed',
  '操作失败': 'errors.operationFailed',
  '参数错误': 'errors.invalidParams',
  '未授权': 'errors.unauthorized',
  '服务暂不可用': 'errors.serviceUnavailable',
};

// Regex patterns for dynamic error messages
const ERROR_PATTERNS = [
  {
    pattern: /USDC\.e -> USDC 兑换失败[：:]\s*(.+)/,
    key: 'errors.usdceToUsdcSwapFailed',
    extractDetail: true,
  },
  {
    pattern: /USDC -> USDC\.e 兑换失败[：:]\s*(.+)/,
    key: 'errors.usdcToUsdceSwapFailed',
    extractDetail: true,
  },
  {
    pattern: /approve 失败[：:]\s*(.+)/,
    key: 'errors.approveFailedDetail',
    extractDetail: true,
  },
  {
    pattern: /USDC 余额不足，当前余额 ([\d.]+) USDC，小于转账金额 ([\d.]+) USDC/,
    key: 'errors.usdcInsufficientBalanceDetail',
    extractParams: ['currentBalance', 'requiredAmount'],
  },
];

/**
 * Translate a backend error code to localized message
 * @param {string} code - The error code from backend (e.g., 'UNAUTHORIZED', 'TOKEN_EXPIRED')
 * @param {function} t - The i18n translation function
 * @param {string} fallbackMsg - Optional fallback message if code not found
 * @returns {string} - Translated message or fallback
 */
export function translateErrorCode(code, t, fallbackMsg = null) {
  if (!code || typeof code !== 'string') {
    return fallbackMsg || code;
  }

  const key = ERROR_CODE_MAP[code];
  if (key) {
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }
  }

  return fallbackMsg || code;
}

/**
 * Translate a backend error message to an i18n key
 * @param {string} message - The error message from backend
 * @param {function} t - The i18n translation function
 * @returns {string} - Translated message or original if no translation found
 */
export function translateErrorMessage(message, t) {
  if (!message || typeof message !== 'string') {
    return message;
  }

  // Check exact matches first
  const exactKey = ERROR_MESSAGE_MAP[message];
  if (exactKey) {
    const translated = t(exactKey);
    // If translation exists and is different from key, use it
    if (translated && translated !== exactKey) {
      return translated;
    }
  }

  // Check pattern matches
  for (const { pattern, key, extractDetail, extractParams } of ERROR_PATTERNS) {
    const match = message.match(pattern);
    if (match) {
      if (extractParams && match.length > 1) {
        // Build interpolation object from named params
        const params = {};
        extractParams.forEach((paramName, index) => {
          params[paramName] = match[index + 1];
        });
        const translated = t(key, params);
        if (translated && translated !== key) {
          return translated;
        }
      } else if (extractDetail && match[1]) {
        // Try to translate the detail part as well
        const detailKey = ERROR_MESSAGE_MAP[match[1].trim()];
        const translatedDetail = detailKey ? t(detailKey) : translateErrorMessage(match[1].trim(), t);
        const baseTranslation = t(key);
        if (baseTranslation && baseTranslation !== key) {
          return `${baseTranslation}: ${translatedDetail}`;
        }
      } else {
        const translated = t(key);
        if (translated && translated !== key) {
          return translated;
        }
      }
    }
  }

  // Return original message if no translation found
  return message;
}

/**
 * Check if a message is a known Chinese error message
 * @param {string} message - The message to check
 * @returns {boolean}
 */
export function isChineseErrorMessage(message) {
  if (!message || typeof message !== 'string') {
    return false;
  }
  
  // Check if message contains Chinese characters
  return /[\u4e00-\u9fa5]/.test(message);
}

export default translateErrorMessage;
